import React, {useCallback, useMemo, useRef} from 'react';
import {LoadingOutlined} from '@ant-design/icons';
import {Spin, Typography} from 'antd';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import DiagramEditorX6 from '@components/diagram/DiagramEditorX6';
import DiagramAiBoardSearch from '@components/diagram/DiagramAiBoardSearch';
import DiagramAiChatPanel from '@components/diagram/DiagramAiChatPanel';
import {diagramsApi} from '@/api/projects';
import {useDiagramEditorData, useDiagramWs, type DiagramVersionState} from '@/hooks/diagram';
import type {DiagramVersionNavigatePayload, ModelPatchOp} from '@/types/api';
import {resolveViewIndexFromModel} from '@/utils/diagram/resolveViewIndex';
import {canEditContent} from '@/utils/workspace/projectRole';

const DiagramEditor: React.FC = () => {
	const editorRef = useRef<{
		save?: () => Promise<void>;
		applyRemotePatch?: (patch: unknown) => void;
		applyVersionNavigate?: (payload: DiagramVersionNavigatePayload) => void;
		applyCatalogChange?: (changes: unknown) => void;
		applyLiveGeometry?: (live: unknown) => void;
		applyLockEvent?: (lock: unknown) => void;
		focusCanvasElementById?: (id: string) => void;
		getModeler?: () => unknown;
	}>(null);
	const [searchParams, setSearchParams] = useSearchParams();
	const location = useLocation();

	const viewIdFromUrl = searchParams.get('view') ?? undefined;
	const handleViewChange = useCallback(
		(viewId: string | undefined) => {
			setSearchParams(prev => {
				const next = new URLSearchParams(prev);
				if (viewId) next.set('view', viewId);
				else next.delete('view');
				return next;
			});
		},
		[setSearchParams]
	);

	const {projectId, diagramId, data} = useDiagramEditorData();
	const {loading, loadingPercent, diagram, projectRole, diagramName, initialXml, initialJson} = data;
	const readOnly = !canEditContent(projectRole);

	const navigate = useNavigate();
	const diagramVersionRef = React.useRef<DiagramVersionState>({
		structuralVersion: 1,
		historyStackCursor: 0,
		historyStackSize: 1,
	});
	const historyAppliedRef = React.useRef<Set<string>>(new Set());

	React.useEffect(() => {
		if (diagram?.structuralVersion != null) {
			diagramVersionRef.current = {
				structuralVersion: diagram.structuralVersion,
				historyStackCursor: diagram.historyStackCursor ?? 0,
				historyStackSize: diagram.historyStackSize ?? 1,
			};
		}
	}, [diagram]);

	const {onApplyPatch, sendCursor, sendLive, sendLock, remoteCursors, objectLocks, clientSessionId} =
		useDiagramWs(
		projectId,
		diagramId,
		loading,
		editorRef,
		diagramVersionRef,
		historyAppliedRef
	);

	const diagramCollaboration = React.useMemo(
		() =>
			projectId && diagramId
					? {
						projectId,
						diagramId,
						versionRef: diagramVersionRef,
						historyAppliedRef,
						applyHistoryReplay: (patch: {patches: ModelPatchOp[]}) => {
							editorRef.current?.applyRemotePatch?.(patch);
						},
					}
				: null,
		[projectId, diagramId]
	);

	const handleSave = useCallback(
		async (content: string) => {
			if (!projectId || !diagramId) return;
			await diagramsApi.updateContent(projectId, diagramId, content);
		},
		[projectId, diagramId]
	);

	if (!projectId || !diagramId) return null;

	const nameFromNavigation = (
		location.state as {diagramName?: string} | null | undefined
	)?.diagramName?.trim();
	const displayName = diagramName.trim() || nameFromNavigation || '';
	const loadingCaption = displayName
		? `Загрузка «${displayName}»...`
		: `Загрузка диаграммы №${diagramId}...`;

	const spinIndicator = useMemo(() => <LoadingOutlined style={{fontSize: 48}} spin />, []);

	const aiViewIndex = useMemo(
		() => resolveViewIndexFromModel(initialJson, viewIdFromUrl),
		[initialJson, viewIdFromUrl]
	);

	return (
		<div
			style={{
				position: 'fixed',
				inset: 0,
				zIndex: 1000,
				background: '#fff',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			{loading ? (
				<div
					style={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 16,
					}}
				>
					<Spin indicator={spinIndicator} percent={loadingPercent} />
					<Typography.Text
						type="secondary"
						style={{textAlign: 'center', maxWidth: 'min(90vw, 480px)'}}
					>
						{loadingCaption}
					</Typography.Text>
				</div>
			) : (
				<div style={{flex: 1, minHeight: 0, position: 'relative'}}>
					<DiagramEditorX6
						ref={editorRef}
						key={`${projectId}-${diagramId}`}
						initialXml={initialXml}
						initialJson={initialJson}
						initialDiagramName={diagramName}
						initialViewId={viewIdFromUrl}
						onViewChange={handleViewChange}
						onSave={handleSave}
						onApplyPatch={readOnly ? undefined : onApplyPatch}
						diagramCollaboration={readOnly ? undefined : diagramCollaboration ?? undefined}
						catalog={{projectId, diagramId}}
						readOnly={readOnly}
						remoteCursors={remoteCursors}
						objectLocks={objectLocks}
						clientSessionId={clientSessionId}
						onCursorSend={sendCursor}
						onLiveSend={readOnly ? undefined : sendLive}
						onLockSend={readOnly ? undefined : sendLock}
						onClose={() => navigate('/')}
						assistantSlot={
							<>
								<DiagramAiBoardSearch
									projectId={projectId}
									diagramId={diagramId}
									viewIndex={aiViewIndex}
									initialJson={initialJson}
									getModeler={() => editorRef.current?.getModeler?.() ?? null}
									focusCanvasElementById={id =>
										editorRef.current?.focusCanvasElementById?.(id)
									}
								/>
								{!readOnly && (
									<DiagramAiChatPanel
										projectId={projectId}
										diagramId={diagramId}
										viewIndex={aiViewIndex}
										getModeler={() => editorRef.current?.getModeler?.() ?? null}
									/>
								)}
							</>
						}
					/>
				</div>
			)}
		</div>
	);
};

export default DiagramEditor;
